import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import Table from "../../../core/common/dataTable/index"
import { masterTemplatesApi, CERT_TYPES, type CertMasterTemplate } from "../../../service/certificate"
import { all_routes } from "../../router/all_routes"

const typeLabel = (v: string) => CERT_TYPES.find(t => t.value === v)?.label || v
const DEFAULT_DEFINITION = { canvas: { width: 1152, height: 960, background: "#ffffff" }, a4_grid: null, layers: [] }

const CertMasterTemplates = () => {
  const routes = all_routes
  const navigate = useNavigate()
  const [items, setItems] = useState<CertMasterTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [showInactive, setShowInactive] = useState(false)

  // New modal
  const [showNew, setShowNew] = useState(false)
  const [newName, setNewName] = useState("")
  const [newType, setNewType] = useState("")
  const [saving, setSaving] = useState(false)

  // Rename modal
  const [renameItem, setRenameItem] = useState<CertMasterTemplate | null>(null)
  const [renameName, setRenameName] = useState("")
  const [renaming, setRenaming] = useState(false)

  // Delete confirm
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Duplicate (optimistic — no modal needed)
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null)

  const fetchItems = async () => {
    setLoading(true)
    try {
      const res = await masterTemplatesApi.list({ includeInactive: showInactive || undefined })
      if (res.success) setItems(res.data)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchItems() }, [showInactive])

  // ── Create ──────────────────────────────────────────────────────────
  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim() || !newType) return toast.error("Name and type are required.")
    setSaving(true)
    try {
      const res = await masterTemplatesApi.create({
        name: newName.trim(),
        certificateType: newType,
        definition: DEFAULT_DEFINITION,
      })
      toast.success("Master template created")
      setShowNew(false)
      setNewName("")
      setNewType("")
      navigate(`${routes.certDesignerEdit.replace(":id", res.data._id)}?master=1`)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Create failed")
    } finally {
      setSaving(false)
    }
  }

  // ── Rename ──────────────────────────────────────────────────────────
  const openRename = (item: CertMasterTemplate) => {
    setRenameItem(item)
    setRenameName(item.name)
  }

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!renameItem || !renameName.trim()) return
    setRenaming(true)
    try {
      await masterTemplatesApi.update(renameItem._id, { name: renameName.trim() })
      toast.success("Renamed")
      setRenameItem(null)
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Rename failed")
    } finally {
      setRenaming(false)
    }
  }

  // ── Duplicate ───────────────────────────────────────────────────────
  const handleDuplicate = async (item: CertMasterTemplate) => {
    setDuplicatingId(item._id)
    try {
      await masterTemplatesApi.duplicate(item._id)
      toast.success(`Duplicated "${item.name}"`)
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Duplicate failed")
    } finally {
      setDuplicatingId(null)
    }
  }

  // ── Delete ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await masterTemplatesApi.remove(deleteId)
      toast.success("Deleted")
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed")
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  // ── Restore ─────────────────────────────────────────────────────────
  const handleRestore = async (id: string) => {
    try {
      await masterTemplatesApi.restore(id)
      toast.success("Restored")
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Restore failed")
    }
  }

  // ── Columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v: string, r: CertMasterTemplate) =>
        r.isActive ? (
          <Link to={`${routes.certDesignerEdit.replace(":id", r._id)}?master=1`} className="link-primary fw-semibold">
            {v}
          </Link>
        ) : (
          <span className="text-muted">{v}</span>
        ),
    },
    {
      title: "Linked Templates",
      dataIndex: "linkedTemplates",
      render: (v: number) => (
        <span className={`fw-semibold ${v > 0 ? "text-success" : "text-muted"}`}>{v ?? 0}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "certificateType",
      render: (v: string) => <span className="badge bg-primary-transparent">{typeLabel(v)}</span>,
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      render: (v: string) => (v ? new Date(v).toLocaleDateString() : "—"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v: boolean) =>
        v ? (
          <span className="badge bg-success-transparent text-success">Active</span>
        ) : (
          <span className="badge bg-secondary-transparent text-secondary">Inactive</span>
        ),
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (_: any, r: CertMasterTemplate) =>
        r.isActive ? (
          <div className="d-flex gap-2 flex-wrap">
            <Link
              to={`${routes.certDesignerEdit.replace(":id", r._id)}?master=1`}
              className="btn btn-sm btn-outline-primary"
              title="Design"
            >
              <i className="ti ti-brush me-1" />Design
            </Link>
            <button
              className="btn btn-sm btn-outline-secondary"
              title="Rename"
              onClick={() => openRename(r)}
            >
              <i className="ti ti-pencil me-1" />Rename
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              title="Duplicate"
              disabled={duplicatingId === r._id}
              onClick={() => handleDuplicate(r)}
            >
              {duplicatingId === r._id ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : (
                <i className="ti ti-copy me-1" />
              )}
              Duplicate
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              title="Delete"
              onClick={() => setDeleteId(r._id)}
            >
              <i className="ti ti-trash" />
            </button>
          </div>
        ) : (
          <button className="btn btn-sm btn-outline-success" onClick={() => handleRestore(r._id)}>
            <i className="ti ti-rotate me-1" />Restore
          </button>
        ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Master Templates</h3>
            <p className="text-muted mb-0">Reusable base designs that templates can inherit from</p>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="showInactive"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label text-muted small" htmlFor="showInactive">
                Show inactive
              </label>
            </div>
            <button className="btn btn-primary" onClick={() => setShowNew(true)}>
              <i className="ti ti-square-rounded-plus me-2" />New Master Template
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table dataSource={items} columns={columns} loading={loading} />
          </div>
        </div>
      </div>

      {/* ── New template modal ── */}
      {showNew && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">New Master Template</h4>
                  <button type="button" className="btn-close" onClick={() => setShowNew(false)} />
                </div>
                <form onSubmit={create}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Name *</label>
                      <input
                        className="form-control"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Certificate Type *</label>
                      <select className="form-select" value={newType} onChange={e => setNewType(e.target.value)}>
                        <option value="">Select type...</option>
                        {CERT_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={() => setShowNew(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Creating…" : "Create & Design"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* ── Rename modal ── */}
      {renameItem && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Rename Master Template</h4>
                  <button type="button" className="btn-close" onClick={() => setRenameItem(null)} />
                </div>
                <form onSubmit={handleRename}>
                  <div className="modal-body">
                    <label className="form-label">Name *</label>
                    <input
                      className="form-control"
                      value={renameName}
                      onChange={e => setRenameName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={() => setRenameItem(null)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={renaming}>
                      {renaming ? "Saving…" : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* ── Delete confirm modal ── */}
      {deleteId && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center p-4">
                  <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
                  <h4 className="mt-2">Delete Master Template?</h4>
                  <p className="text-muted small">
                    This is a soft delete — it can be restored later via "Show inactive".
                  </p>
                  <div className="d-flex justify-content-center gap-2 mt-3">
                    <button className="btn btn-light" onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                      {deleting ? "Deleting…" : "Yes, Delete"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  )
}

export default CertMasterTemplates
